import React, { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import Axios from "axios";
import Table from "react-bootstrap/Table";
import "./BarcodeScannerAndSearch.css";
import logUserActivity from "./Utils/LogUserActivity";

const qrcodeRegionId = "html5qr-code-full-region";

const PiracyBarcode = () => {
  const [lastScannedBarcode, setLastScannedBarcode] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isInputMode, setIsInputMode] = useState(false);
  const scannerRef = useRef(null); // ✅ Corrected to useRef
  const [data, setData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [scanCount, setScanCount] = useState(0);

  const [shelfMode, setShelfMode] = useState(""); // normal | audit
  const [dataReady, setDataReady] = useState(false);

  const [scanningLocked, setScanningLocked] = useState(false);

  const [scannedBarcodes, setScannedBarcodes] = useState(new Set());
  const [duplicateAlert, setDuplicateAlert] = useState(false);
  const [scanCooldown, setScanCooldown] = useState(false);
  const lastScannedRef = useRef("");
  const [lastScanTime, setLastScanTime] = useState(0);

  useEffect(() => {
    fetchShelfData();
  }, []);

  useEffect(() => {
    setScannedBarcodes([]);
    setLastScannedBarcode("");
    setScanCount(0);
    lastScannedRef.current = "";
  }, [shelfMode]);

  useEffect(() => {
    if (shelfMode && !isInputMode) {
      checkAndStartScanner();
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(console.error);
        scannerRef.current.clear();
      }
    };
  }, [shelfMode, isInputMode]);

  const fetchShelfData = () => {
    Axios.get("https://nodei.ssccglpinnacle.com/getManageshelf")
      .then((res) => {
        setData(res.data);
        setDataReady(true);
      })
      .catch(console.error);
  };

  const checkAndStartScanner = async () => {
    try {
      const permission = await navigator.permissions.query({ name: "camera" });
      if (permission.state === "granted" || permission.state === "prompt") {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        startScanner();
        stream.getTracks().forEach((track) => track.stop());
      } else {
        alert("Camera permission denied.");
      }
    } catch (err) {
      console.error("Camera access error:", err);
    }
  };

  const startScanner = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === "videoinput");
      if (!videoDevices.length) {
        alert("No camera found.");
        return;
      }

      const backCamera =
        videoDevices.find((d) => d.label.toLowerCase().includes("back"))
          ?.deviceId || videoDevices[0].deviceId;

      const config = {
        fps: 10,
        qrbox: { width: 300, height: 100 },
        rememberLastUsedCamera: false,
        videoConstraints: { facingMode: "environment" },
      };

      scannerRef.current = new Html5Qrcode(qrcodeRegionId);
      await scannerRef.current.start(
        backCamera,
        config,
        handleScanSuccess,
        handleScanError
      );
    } catch (error) {
      console.error("Scanner start error:", error);
    }
  };

  const handleScanSuccess = (decodedText) => {
    const trimmed = decodedText.trim();

    if (scanCooldown || lastScannedRef.current === trimmed) return;

    if (scannedBarcodes.includes(trimmed)) {
      setDuplicateAlert(true);
      setTimeout(() => setDuplicateAlert(false), 2000);
      lastScannedRef.current = trimmed;
      return;
    }

    setScanCooldown(true);
    lastScannedRef.current = trimmed;
    setTimeout(() => {
      setScanCooldown(false);
      lastScannedRef.current = "";
    }, 2000);

    setScannedBarcodes((prev) => [...prev, trimmed]);
    setLastScannedBarcode(trimmed);
    setScanCount((prev) => prev + 1);
    handleSearch(trimmed);
  };

  const handleScanError = (error) => {
    console.warn("Scan Error:", error);
  };

  const handleSearch = async (barcode) => {
    try {
      const response = await Axios.get(
        `https://nodei.ssccglpinnacle.com/getKey/${barcode}`
      );
      const keyToSearch = response.data?.key?.trim() || barcode;
      const filtered = data.filter((item) =>
        item.barcodeData.includes(keyToSearch)
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || inputValue === lastScannedBarcode) {
      setDuplicateAlert(true);
      setTimeout(() => setDuplicateAlert(false), 2000);
      return;
    }
    setLastScannedBarcode(inputValue.trim());
    setScanCount((prev) => prev + 1);
    handleSearch(inputValue.trim());
    setInputValue("");
  };

  const handleSwitchMode = () => {
    setIsInputMode((prev) => !prev);
  };

  const handleRowSubmit = async (row, scanCount) => {
    const entered = row.enteredCount;
    if (
      entered === undefined ||
      entered === null ||
      entered === "" ||
      isNaN(entered)
    ) {
      alert(
        "❗ Physical count is missing! Please enter the number of books before submitting."
      );
      return;
    }

    const payload = {
      rackno: (row.rackno || "").trim(),
      shelfno: (row.shelfno || "").trim(),
      sku: (row.sku || "").trim(),
      title: (row.title || "").trim(),
      Medium: (row.Medium || "").trim(),
      edition: (row.edition || "").trim(),
      presentquantity: Number(row.presentquantity) || 0,
      scanCount: Number(scanCount) || 0,
      physicalCount: Number(entered),
      freeze: Number(row.presentquantity) || 0,
    };

    console.log("📤 Payload being sent to PUT API:", payload);

    try {
      const res = await Axios.put(
        "https://nodei.ssccglpinnacle.com/updateScanValue",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      await logUserActivity("AUDIT_SHELF_SCAN_SUBMIT", payload);

      console.log("✅ PUT response:", res.data);
      alert("✅ Scan data saved successfully!");
      window.location.reload();
    } catch (error) {
      const errDetails =
        error?.response?.data || error.message || "Unknown error";
      console.error("❌ PUT error details:", errDetails);
      alert(
        "❌ Failed to save scan data.\n\n" + JSON.stringify(errDetails, null, 2)
      );
    }
  };

  return (
    <div className="app-container">
      {!shelfMode ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "40px",
            marginTop: "40px",
            flexWrap: "wrap",
          }}
        >
          <div
            onClick={() => setShelfMode("normal")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              transition: "transform 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.firstChild.style.backgroundColor = "#45a049";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.firstChild.style.backgroundColor = "#4CAF50";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Normal Shelf"
          >
            <div
              style={{
                width: "70px",
                height: "70px",
                backgroundColor: "#4CAF50",
                color: "#fff",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "32px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                transition: "background-color 0.3s",
              }}
            >
              🗂️
            </div>
            <div
              style={{
                marginTop: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Normal Shelf
            </div>
          </div>

          <div
            onClick={() => setShelfMode("audit")}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
              transition: "transform 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.firstChild.style.backgroundColor = "#1e88e5";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.firstChild.style.backgroundColor = "#2196F3";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Audit Shelf"
          >
            <div
              style={{
                width: "70px",
                height: "70px",
                backgroundColor: "#2196F3",
                color: "#fff",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "32px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                transition: "background-color 0.3s",
              }}
            >
              🔍
            </div>
            <div
              style={{
                marginTop: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              Audit Shelf
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "20px",
              flexWrap: "wrap",
            }}
          >
            <button
              className="switch-mode-button"
              onClick={handleSwitchMode}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                backgroundColor: "#4CAF50",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                marginRight: "15px",
              }}
            >
              {isInputMode ? "Switch to Scanner Mode" : "Switch to Input Mode"}
            </button>
            <div
              style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}
            >
              📊 Scan Count:{" "}
              <span style={{ color: "#007bff" }}>{scanCount}</span>
            </div>
          </div>

          {!isInputMode ? (
            <div id={qrcodeRegionId} className="qr-reader" />
          ) : (
            <div className="input-container">
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Enter barcode"
              />
              <button type="submit" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          )}

          {duplicateAlert && (
            <div
              style={{ marginTop: "10px", color: "red", textAlign: "center" }}
            >
              ⚠️ Already Scanned!
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="findshelf-wrapper">
              <h3 style={{ textAlign: "center" }}>Book Shelf Status</h3>
              <ul
                style={{
                  listStyle: "none",
                  padding: "0",
                  marginTop: "30px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  borderRadius: "10px",
                  border: "1px solid #dcdcdc",
                  fontFamily: "'Segoe UI', sans-serif",
                  backgroundColor: "#fafafa",
                }}
              >
                {searchResults.map((row, idx) => (
                  <li
                    key={idx}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f4f7fb",
                      padding: "20px",
                      borderBottom: "1px solid #e2e2e2",
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px",
                      }}
                    >
                      {[
                        { label: "Warehouse", value: row.Warehouse },
                        { label: "Rack No", value: row.rackno },
                        { label: "Shelf No", value: row.shelfno },
                        { label: "SKU", value: row.sku },
                        { label: "Title", value: row.title },
                        { label: "Medium", value: row.Medium },
                        { label: "Edition", value: row.edition },
                        {
                          label: "Book ID",
                          value: lastScannedBarcode || inputValue,
                        },
                        ...(shelfMode === "audit"
                          ? [
                              {
                                label: "Stock Qty",
                                value: row.presentquantity,
                              },
                              { label: "Scan Count", value: scanCount },
                            ]
                          : []),
                      ].map((item, i) => (
                        <div
                          key={i}
                          style={{
                            flex: "1 0 240px",
                            backgroundColor: "#fff",
                            border: "1px solid #e0e0e0",
                            borderRadius: "6px",
                            padding: "10px 14px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "14px",
                          }}
                        >
                          <strong style={{ color: "#444" }}>
                            {item.label}
                          </strong>
                          <span style={{ color: "#555" }}>{item.value}</span>
                        </div>
                      ))}
                    </div>

                    {shelfMode === "audit" && (
                      <div
                        style={{
                          marginTop: "16px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="number"
                          min="0"
                          placeholder="Enter count"
                          style={{
                            width: "140px",
                            padding: "8px",
                            fontSize: "14px",
                            textAlign: "center",
                            border: "1px solid #bfbfbf",
                            borderRadius: "5px",
                            outline: "none",
                          }}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => {
                            const updated = [...searchResults];
                            updated[idx].enteredCount = Number(e.target.value);
                            setSearchResults(updated);
                          }}
                        />
                        <button
                          style={{
                            padding: "8px 16px",
                            fontSize: "14px",
                            backgroundColor: "#007bff",
                            color: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseOver={(e) =>
                            (e.target.style.backgroundColor = "#0069d9")
                          }
                          onMouseOut={(e) =>
                            (e.target.style.backgroundColor = "#007bff")
                          }
                          onClick={() => handleRowSubmit(row, scanCount)}
                        >
                          Submit
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PiracyBarcode;
